import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import authFetch from '../utils/api';
import './Mileage.css';

const Mileage = () => {
  const [summary, setSummary] = useState(null);
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [homeAddress, setHomeAddress] = useState(null);
  const [message, setMessage] = useState(null);
  const [routeDate, setRouteDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [calculatingRoute, setCalculatingRoute] = useState(false);

  useEffect(() => {
    fetchData();
  }, [selectedYear]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [summaryRes, tripsRes, homeRes] = await Promise.all([
        authFetch(`/api/mileage/summary?year=${selectedYear}`),
        authFetch(`/api/mileage/trips?year=${selectedYear}`),
        fetch('/api/mileage/home-address')
      ]);

      const summaryData = await summaryRes.json();
      const tripsData = await tripsRes.json();
      const homeData = await homeRes.json();

      setSummary(summaryData);
      setTrips(tripsData);
      setHomeAddress(homeData.address);
    } catch (error) {
      console.error('Error fetching mileage data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await fetch(`/api/mileage/export?year=${selectedYear}`);
      const data = await response.json();

      // Create CSV content
      const headers = ['Date', 'Starting Location', 'Destination', 'Business Purpose', 'Miles', 'Trip Type'];
      const rows = data.trips.map(trip => [
        trip.Date,
        `"${trip['Starting Location']}"`,
        `"${trip.Destination}"`,
        `"${trip['Business Purpose'] || ''}"`,
        trip.Miles,
        trip['Trip Type']
      ]);

      const csv = [
        headers.join(','),
        ...rows.map(row => row.join(','))
      ].join('\n');

      // Add summary at the end
      const csvWithSummary = csv + `\n\nTotal Miles,${data.totalMiles}\nEstimated Deduction (@ $0.67/mile),$${data.estimatedDeduction}`;

      // Download
      const blob = new Blob([csvWithSummary], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mileage-${selectedYear}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);

      setMessage({ type: 'success', text: 'Mileage report exported!' });
    } catch (error) {
      console.error('Error exporting:', error);
      setMessage({ type: 'error', text: 'Failed to export mileage report' });
    }
  };

  const handleDeleteTrip = async (tripId) => {
    if (!window.confirm('Are you sure you want to delete this trip?')) return;

    try {
      const response = await authFetch(`/api/mileage/trips/${tripId}`, { method: 'DELETE' });
      if (response.ok) {
        fetchData();
        setMessage({ type: 'success', text: 'Trip deleted' });
      }
    } catch (error) {
      console.error('Error deleting trip:', error);
      setMessage({ type: 'error', text: 'Failed to delete trip' });
    }
  };

  const handleCalculateDailyRoute = async () => {
    if (!routeDate) {
      setMessage({ type: 'error', text: 'Please select a date' });
      return;
    }

    setCalculatingRoute(true);
    setMessage(null);

    try {
      const response = await authFetch('/api/mileage/calculate-daily-route', {
        method: 'POST',
        body: JSON.stringify({ date: routeDate })
      });

      const data = await response.json();

      if (response.ok) {
        setMessage({
          type: 'success',
          text: `Daily route calculated! ${data.appointmentsCount} appointments, ${data.totalMiles} total miles.`
        });
        fetchData();
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to calculate route' });
      }
    } catch (error) {
      console.error('Error calculating daily route:', error);
      setMessage({ type: 'error', text: 'Failed to calculate daily route' });
    } finally {
      setCalculatingRoute(false);
    }
  };

  const getYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear; i >= currentYear - 5; i--) {
      years.push(i.toString());
    }
    return years;
  };

  const getMonthName = (monthNum) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return months[parseInt(monthNum) - 1];
  };

  // IRS standard mileage rate for 2024
  const IRS_RATE = 0.67;

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="mileage-page">
      <div className="page-header">
        <h1 className="page-header__title">Mileage Tracker</h1>
        <div className="page-header__actions">
          <select
            className="form-select"
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            style={{ width: 'auto' }}
          >
            {getYearOptions().map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
          <button className="btn btn--primary" onClick={handleExport}>
            Export for Taxes
          </button>
        </div>
      </div>

      {message && (
        <div className={`alert alert--${message.type}`}>
          {message.text}
          <button className="alert__close" onClick={() => setMessage(null)}>&times;</button>
        </div>
      )}

      {!homeAddress && (
        <div className="alert alert--warning">
          <strong>Home address not set!</strong> Go to Settings to set your home/office address before tracking mileage.
        </div>
      )}

      {/* Daily Route Calculator */}
      {homeAddress && (
        <div className="daily-route-section">
          <h3>Calculate Daily Route</h3>
          <p className="daily-route-desc">
            Calculate mileage for all appointments on a single day. Routes are calculated in appointment time order:
            Office → Appointment 1 → Appointment 2 → ... → Office
          </p>
          <div className="daily-route-form">
            <input
              type="date"
              className="form-input"
              value={routeDate}
              onChange={(e) => setRouteDate(e.target.value)}
            />
            <button
              className="btn btn--primary"
              onClick={handleCalculateDailyRoute}
              disabled={calculatingRoute}
            >
              {calculatingRoute ? 'Calculating...' : 'Calculate Route'}
            </button>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="mileage-summary-grid">
        <div className="mileage-card">
          <div className="mileage-card__label">Total Miles ({selectedYear})</div>
          <div className="mileage-card__value">{summary?.totalMiles || 0}</div>
        </div>
        <div className="mileage-card">
          <div className="mileage-card__label">Total Trips</div>
          <div className="mileage-card__value">{summary?.tripCount || 0}</div>
        </div>
        <div className="mileage-card mileage-card--highlight">
          <div className="mileage-card__label">Estimated Deduction</div>
          <div className="mileage-card__value">
            ${((summary?.totalMiles || 0) * IRS_RATE).toFixed(2)}
          </div>
          <div className="mileage-card__sublabel">@ ${IRS_RATE}/mile IRS rate</div>
        </div>
      </div>

      {/* Monthly Breakdown */}
      {summary?.monthlyBreakdown?.length > 0 && (
        <div className="mileage-section">
          <h2>Monthly Breakdown</h2>
          <div className="monthly-chart">
            {summary.monthlyBreakdown.map((month) => (
              <div key={month.month} className="monthly-bar">
                <div className="monthly-bar__label">{getMonthName(month.month)}</div>
                <div className="monthly-bar__track">
                  <div
                    className="monthly-bar__fill"
                    style={{
                      width: `${Math.min((month.total_miles / Math.max(...summary.monthlyBreakdown.map(m => m.total_miles))) * 100, 100)}%`
                    }}
                  ></div>
                </div>
                <div className="monthly-bar__value">{month.total_miles} mi</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Trip List */}
      <div className="mileage-section">
        <h2>All Trips ({selectedYear})</h2>
        {trips.length === 0 ? (
          <div className="empty-state">
            <p>No trips recorded for {selectedYear}</p>
            <p className="text-gray">Trips are automatically created when you calculate mileage for appointments.</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>From</th>
                  <th>To</th>
                  <th>Purpose</th>
                  <th>Miles</th>
                  <th>Type</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {trips.map((trip) => (
                  <tr key={trip.id}>
                    <td>{format(new Date(trip.trip_date), 'MMM d, yyyy')}</td>
                    <td className="trip-address">
                      {trip.start_address}
                      <span className="trip-address__city">{trip.start_city}, {trip.start_state}</span>
                    </td>
                    <td className="trip-address">
                      {trip.end_address}
                      <span className="trip-address__city">{trip.end_city}, {trip.end_state}</span>
                    </td>
                    <td>{trip.business_purpose || '-'}</td>
                    <td className="trip-miles">{trip.miles}</td>
                    <td>
                      <span className={`badge badge--${
                        trip.trip_type === 'to_appointment' ? 'scheduled' :
                        trip.trip_type === 'return_home' ? 'completed' :
                        trip.trip_type === 'between_appointments' ? 'confirmed' : 'draft'
                      }`}>
                        {trip.trip_type === 'to_appointment' ? 'To Apt' :
                         trip.trip_type === 'return_home' ? 'Return' :
                         trip.trip_type === 'between_appointments' ? 'Between' : 'Manual'}
                      </span>
                    </td>
                    <td>
                      <button
                        className="btn btn--danger btn--small btn--icon"
                        onClick={() => handleDeleteTrip(trip.id)}
                      >
                        &times;
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* IRS Info */}
      <div className="mileage-section mileage-info">
        <h3>IRS Mileage Tracking Requirements</h3>
        <ul>
          <li><strong>Date</strong> of each trip</li>
          <li><strong>Starting location</strong> and <strong>destination</strong></li>
          <li><strong>Business purpose</strong> of the trip</li>
          <li><strong>Miles driven</strong></li>
        </ul>
        <p className="mileage-info__note">
          This tracker captures all required information for IRS documentation.
          Export your data regularly and keep records for at least 3 years.
        </p>
      </div>
    </div>
  );
};

export default Mileage;
